import { useCallback, useEffect, useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ HOW MUCH MARKET IS LEFT ═══
 *
 * ⚠ THE SENTENCE THIS EXISTS TO PRODUCE: "there are 61 schools in your districts and you have
 * approached 9." Without a directory that cannot be said at any price, and without it "I should get
 * more schools" is a feeling rather than a plan.
 *
 * ⚠ A DIRECTORY ROW IS NOT A PROSPECT. There are thousands of these and tens of those. Promoting is
 * one tap and carries the facts across; everything else stays out of the pipeline, which is the one
 * screen whose job is to tell you what to do this morning.
 */

interface Entry {
  id: string; name: string; village: string | null; block: string | null;
  district: string | null; state: string | null;
  udiseCode: string | null; aisheCode: string | null;
  board: string; boardLabel: string; management: string; managementLabel: string;
  studentCount: number | null; facultyCount: number | null; establishedYear: number | null;
  source: string; prospectId: string | null; dismissed: boolean;
  dismissedReason: string | null; isOpen: boolean;
}

interface District {
  name: string; total: number; contacted: number; won: number;
  dismissed: number; untouched: number; untouchedStudents: number;
}

interface Coverage {
  total: number; contacted: number; won: number; untouched: number;
  districts: District[]; notes: string[];
}

interface Preview {
  wouldAdd: number; wouldRefresh: number; problems: number;
  unmappedColumns: string[]; mappedFields: string[];
  firstProblems: string[]; sample: Entry[];
}

export function MarketMap({ onPromoted }: { onPromoted: () => void }) {
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [rows, setRows] = useState<Entry[]>([]);
  const [district, setDistrict] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const reload = useCallback(async () => {
    const q = new URLSearchParams({ openOnly: 'true', take: '40' });
    if (district) q.set('district', district);
    if (search.trim()) q.set('search', search.trim());
    const [c, list] = await Promise.all([
      salesApi.get<Coverage>('/api/platform/sales/directory/coverage'),
      salesApi.get<Entry[]>(`/api/platform/sales/directory?${q}`),
    ]);
    // ⚠ GUARDED. A response that is not an array — an older build, a proxy returning an error
    // object with a 200 — used to blank the entire screen, coverage included, because .map threw.
    // A screen that shows nothing is indistinguishable from a screen with no data.
    setCoverage(c && Array.isArray(c.districts) ? c : null);
    setRows(Array.isArray(list) ? list : []);
  }, [district, search]);

  useEffect(() => { reload().catch(() => undefined); }, [reload]);

  const promote = async (e: Entry) => {
    setBusy(e.id); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/directory/${e.id}/promote`, {});
      await reload();
      onPromoted();
    } catch (err) {
      setError(err instanceof PlatformApiError ? err.message : 'Could not add it to the pipeline.');
    } finally { setBusy(null); }
  };

  const dismiss = async (e: Entry) => {
    const reason = window.prompt(`Why are you leaving ${e.name} off the list?`, 'Too small.');
    if (!reason) return;
    setBusy(e.id);
    try { await salesApi.post(`/api/platform/sales/directory/${e.id}/dismiss`, { reason }); await reload(); }
    catch { /* the list reloads either way */ }
    finally { setBusy(null); }
  };

  return (
    <div>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{error}</p>}

      {coverage && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 6 }}>The market</div>
          {/* ⚠ SENTENCES, NOT A PERCENTAGE. "62% covered" tells a founder nothing they can act on;
              "Udupi is the most untouched — 34 schools, about 21,000 children" is Thursday. */}
          <ul style={{ margin: '0 0 12px', paddingLeft: 16 }}>
            {coverage.notes.map((n, i) => (
              <li key={i} style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 3 }}>{n}</li>
            ))}
          </ul>

          {coverage.districts.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => setDistrict('')}
                      style={{ fontWeight: district === '' ? 650 : 400 }}>
                Everywhere
              </button>
              {coverage.districts.map((d) => (
                <button key={d.name} className="btn" onClick={() => setDistrict(d.name)}
                        style={{ fontWeight: district === d.name ? 650 : 400 }}>
                  {d.name} · {d.untouched} left
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" placeholder="Search name, village or taluk…" value={search}
               style={{ flex: '1 1 240px' }} onChange={(e) => setSearch(e.target.value)} />
        <button className="btn" onClick={() => setImporting(!importing)}>
          {importing ? 'Close' : 'Import a register'}
        </button>
      </div>

      {importing && <ImportPanel onDone={() => { setImporting(false); void reload(); }} />}

      {rows.length === 0
        ? <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            Nothing on the map here yet.
          </div>
        : rows.map((e) => (
          <div key={e.id} className="card"
               style={{ padding: 12, marginBottom: 6, display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{e.name}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {/* ⚠ DEDUPED. In the register a village, its block and its district are very often
                    the same word — "Kundapura · Kundapura · Udupi" — and repeating it makes a row
                    read like a data-entry error rather than a place. */}
                {[...new Set([e.village, e.block, e.district].filter(Boolean) as string[])].join(' · ')}
                {e.boardLabel && e.board !== 'Unknown' ? ` · ${e.boardLabel}` : ''}
                {e.management !== 'Unknown' ? ` · ${e.managementLabel}` : ''}
                {/* ⚠ THE PROVENANCE IS ON EVERY ROW. A figure from a government release and one a
                    principal told you are different kinds of fact, and a screen that cannot tell
                    them apart teaches you to distrust both. */}
                {` · ${e.source}`}
              </div>
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--ink-2)', minWidth: 110 }}>
              {e.studentCount ? `${e.studentCount.toLocaleString('en-IN')} students` : 'size unknown'}
              {e.establishedYear ? ` · est. ${e.establishedYear}` : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-accent" disabled={busy === e.id} onClick={() => void promote(e)}>
                {busy === e.id ? '…' : 'Pursue'}
              </button>
              <button className="btn" disabled={busy === e.id} onClick={() => void dismiss(e)}>Not this one</button>
            </div>
          </div>
        ))}
    </div>
  );
}

function ImportPanel({ onDone }: { onDone: () => void }) {
  const [csv, setCsv] = useState('');
  const [source, setSource] = useState('UDISE 2024-25');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (commit: boolean) => {
    setBusy(true); setError(null);
    try {
      const p = await salesApi.post<Preview>('/api/platform/sales/directory/import',
        { csv, source, commit });
      setPreview(p);
      if (commit) onDone();
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not read the file.');
    } finally { setBusy(false); }
  };

  const onFile = async (f: File | null) => {
    if (!f) return;
    setCsv(await f.text());
    setPreview(null);
  };

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ fontWeight: 650, marginBottom: 4 }}>Import a register</div>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px' }}>
        A UDISE or AISHE export, as CSV. It reads the headings it recognises and tells you which it
        did not — nothing is saved until you press Import.
      </p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e.target.files?.[0] ?? null)} />
        {/* ⚠ REQUIRED, AND IT ENDS UP ON EVERY ROW. A row whose provenance is unknown cannot be
            trusted against something a principal told you, and cannot be refreshed next year. */}
        <input className="input" value={source} placeholder="Which release, e.g. UDISE 2024-25"
               style={{ flex: '1 1 200px' }} onChange={(e) => setSource(e.target.value)} />
      </div>

      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button className="btn" disabled={!csv || !source.trim() || busy} onClick={() => void run(false)}>
          {busy ? 'Reading…' : 'Check the file'}
        </button>
        <button className="btn btn-accent"
                disabled={!preview || preview.wouldAdd + preview.wouldRefresh === 0 || busy}
                onClick={() => void run(true)}>
          Import {preview ? preview.wouldAdd + preview.wouldRefresh : ''}
        </button>
      </div>

      {preview && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
          <div style={{ marginBottom: 6 }}>
            <strong>{preview.wouldAdd}</strong> new · <strong>{preview.wouldRefresh}</strong> already
            on the map (would be refreshed) · <strong>{preview.problems}</strong> skipped
          </div>
          <div style={{ marginBottom: 6, color: 'var(--ink-3)' }}>
            Understood: {preview.mappedFields.join(', ') || 'nothing'}
          </div>
          {preview.unmappedColumns.length > 0 && (
            <div style={{ marginBottom: 6, color: 'var(--ink-3)' }}>
              {/* ⚠ Shown loudly: a missing enrolment column is the difference between a map and a
                  list of names, and it is invisible until somebody looks for it. */}
              Not used: {preview.unmappedColumns.slice(0, 12).join(', ')}
            </div>
          )}
          {preview.firstProblems.length > 0 && (
            <ul style={{ margin: '0 0 6px', paddingLeft: 16 }}>
              {preview.firstProblems.map((p, i) => (
                <li key={i} style={{ marginBottom: 2, color: 'var(--ink-3)' }}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
